import React, { useLayoutEffect, useRef } from 'react';
import { useAnimationActivity } from 'contexts/AnimationActivityContext';
import gsap from 'gsap';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useTableSnap } from 'hooks/useTableSnap';

import PokerChip from './PokerChip';

type ChipColor = 'white' | 'red' | 'green' | 'blue' | 'black' | 'gold';

type BetChipsPropType = {
  /** Fallback committed-bet position before the pill is measurable. */
  from: SeatPosition;
  /** Displayed wager, including portrait/tray offsets and table scaling. */
  originElement?: HTMLElement | null;
  /** Use the dramatic all-in variant (more chips, premium colors, bigger arc). */
  allIn?: boolean;
};

const REGULAR_COUNT = 6;
const REGULAR_SIZE = 14;
const ALLIN_COUNT = 12;
const ALLIN_SIZE = 16;
const ALLIN_PALETTE: ChipColor[] = ['gold', 'black', 'red', 'white'];

/**
 * Collect committed bets into the visible pot. Mount with a fresh key for
 * each positive-bet → zero transition, not for each wager.
 *
 * When `allIn` is true, the animation is dramatized: more chips, mixed
 * gold/black/red/white palette, higher burst arc, and slightly weightier
 * flight to convey "all chips on the line".
 */
const BetChips = ({ from, originElement, allIn = false }: BetChipsPropType) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const startPosition = useRef(from).current;
  const { begin } = useAnimationActivity();
  const shouldSnap = useTableSnap();

  const chipCount = allIn ? ALLIN_COUNT : REGULAR_COUNT;
  const chipSize = allIn ? ALLIN_SIZE : REGULAR_SIZE;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const tableEl = container.closest('[data-table-root]') as HTMLElement | null;
    // Landscape multi-run side pots hide the pill beside the boards. Pot marks
    // the visible MP/SP box instead; the fallback supports older preview hosts.
    const potEl = (tableEl?.querySelector('[data-pot-collection-target]') ??
      tableEl?.querySelector('[data-pot-target]')) as HTMLElement | null;
    if (!tableEl || !potEl) return;

    const tableRect = tableEl.getBoundingClientRect();
    if (tableRect.width <= 0 || tableRect.height <= 0) return;
    // Measure before the pill disappears. Keep the origin fixed for this flight
    // so subsequent bet/seat renders cannot move chips into an empty seat.
    const source =
      originElement?.closest('[data-table-root]') === tableEl ? originElement : container;
    const originRect = source.getBoundingClientRect();
    const potRect = potEl.getBoundingClientRect();
    const originX = originRect.left + originRect.width / 2;
    const originY = originRect.top + originRect.height / 2;
    const targetX = potRect.left + potRect.width / 2;
    const targetY = potRect.top + potRect.height / 2;
    container.style.left = `${((originX - tableRect.left) / tableRect.width) * 100}%`;
    container.style.top = `${((originY - tableRect.top) / tableRect.height) * 100}%`;
    // Rects are viewport pixels; GSAP translations are table-local CSS pixels.
    const scaleX = tableRect.width / (tableEl.offsetWidth || tableRect.width);
    const scaleY = tableRect.height / (tableEl.offsetHeight || tableRect.height);
    const dx = (targetX - originX) / scaleX;
    const dy = (targetY - originY) / scaleY;

    const chips = container.querySelectorAll('.bet-chip');
    if (chips.length === 0) return;

    // Snapping (table switch / resync): these chips are already in the pot as
    // far as the player is concerned. Park them at the flight's end state
    // (invisible) so nothing travels across the felt on arrival.
    if (shouldSnap()) {
      gsap.set(chips, { opacity: 0 });
      return;
    }

    // All-in is bigger, higher, weightier. Regular is quick and tight.
    const scatter = allIn ? 26 : 14;
    const delayStep = allIn ? 0.03 : 0.04;
    const arcBase = allIn ? 30 : 20;
    const arcRand = allIn ? 22 : 15;
    const burstFraction = allIn ? 0.3 : 0.2;
    const burstScale = allIn ? 1.4 : 1.2;
    const flyDuration = allIn ? 0.55 : 0.45;
    const burstDuration = allIn ? 0.22 : 0.18;
    const landDelay = burstDuration + flyDuration;

    // Mark the table busy for the flight so the seat rotation holds off.
    const endActivity = begin();
    const tl = gsap.timeline({ onComplete: endActivity });

    chips.forEach((chip, i) => {
      const sx = (Math.random() - 0.5) * scatter;
      const sy = (Math.random() - 0.5) * scatter;
      const delay = i * delayStep;
      const arcY = -(arcBase + Math.random() * arcRand);
      const initRot = Math.random() * 30 - 15;
      const burstRot = Math.random() * (allIn ? 140 : 80) - (allIn ? 70 : 40);

      gsap.set(chip, { x: 0, y: 0, opacity: 0, scale: 0.5, rotation: initRot });

      // Burst up out of the stack
      tl.to(
        chip,
        {
          x: dx * burstFraction + sx,
          y: arcY,
          opacity: 1,
          scale: burstScale,
          rotation: burstRot,
          duration: burstDuration,
          ease: 'power2.out',
          delay
        },
        0
      )
        // Fly to the pot
        .to(
          chip,
          {
            x: dx + sx,
            y: dy + sy,
            scale: 0.85,
            rotation: 0,
            duration: flyDuration,
            ease: 'power2.in'
          },
          burstDuration + delay
        )
        // Land and fade
        .to(
          chip,
          {
            opacity: 0,
            scale: 0.5,
            duration: 0.15,
            ease: 'power1.in'
          },
          landDelay + delay
        );
    });

    return () => {
      endActivity();
      tl.kill();
    };
    // Run exactly once on mount. The parent remounts BetChips via a fresh
    // `key` whenever a new flight is needed; including `from` / `allIn` here
    // would re-fire the animation on every window resize (since positions
    // are recomputed from container size).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!from) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: `${startPosition.leftPct}%`,
        top: `${startPosition.topPct}%`,
        // Same plane as the static bet pill and the dealer button (z5) — a
        // chip in flight is still an object on the felt, under the POV hand
        // (z6). Keeps the pill→flight handoff z-stable.
        zIndex: 5,
        pointerEvents: 'none'
      }}>
      {Array.from({ length: chipCount }).map((_, i) => {
        const color: ChipColor = allIn ? ALLIN_PALETTE[i % ALLIN_PALETTE.length] : 'red';
        return (
          <div
            key={i}
            className='bet-chip'
            style={{ position: 'absolute', left: -chipSize / 2, top: -chipSize / 2, opacity: 0 }}>
            <PokerChip size={chipSize} color={color} />
          </div>
        );
      })}
    </div>
  );
};

export default BetChips;
